import type { SVGProps } from 'react';

/**
 * Conjunto de ícones em SVG inline.
 *
 * Sem biblioteca de ícones de propósito: são poucos ícones, e definir os traços
 * localmente garante peso de linha e cantos consistentes em todo o painel —
 * detalhe que mais afeta a percepção de acabamento em uma tabela densa.
 * `currentColor` + `strokeWidth` uniforme (1.75) mantêm a coerência visual.
 */

type IconProps = SVGProps<SVGSVGElement>;

function Icon({ children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export const CheckIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M20 6 9 17l-5-5" />
  </Icon>
);

export const CheckAllIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M2 13l4 4L15 8" />
    <path d="M9 17l4 4 9-11" />
  </Icon>
);

export const CloseIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Icon>
);

export const ChevronLeftIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M15 18l-6-6 6-6" />
  </Icon>
);

export const ChevronRightIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9 18l6-6-6-6" />
  </Icon>
);

export const ChevronsLeftIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M11 18l-6-6 6-6M18 18l-6-6 6-6" />
  </Icon>
);

export const ChevronsRightIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M13 18l6-6-6-6M6 18l6-6-6-6" />
  </Icon>
);

// Espelho do SignOutIcon: a seta aponta para dentro da porta. Os dois compartilham
// o mesmo desenho de propósito — entrar e sair ficam reconhecíveis como par.
export const SignInIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <path d="M10 17l5-5-5-5M15 12H3" />
  </Icon>
);

export const SignOutIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5M21 12H9" />
  </Icon>
);

export const RefreshIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
    <path d="M21 3v6h-6" />
  </Icon>
);

export const MailIcon = (props: IconProps) => (
  <Icon {...props}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3.5 7 8.5 6 8.5-6" />
  </Icon>
);

export const ShieldIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 3l7.5 3v5.5c0 4.4-3 8.3-7.5 9.5-4.5-1.2-7.5-5.1-7.5-9.5V6z" />
    <path d="m9.5 12 1.8 1.8 3.4-3.6" />
  </Icon>
);

export const UsersIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M16 19v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 17.5V19" />
    <circle cx="10" cy="8" r="3.25" />
    <path d="M20 19v-1.5a3.5 3.5 0 0 0-2.75-3.42M16.5 5.2a3.25 3.25 0 0 1 0 5.6" />
  </Icon>
);

export const InboxIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3 12h4l2 3h6l2-3h4" />
    <path d="M5.5 5h13l2.5 7v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5z" />
  </Icon>
);

export const AlertIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 9v4M12 16.5v.5" />
    <path d="M10.3 4.3 2.8 17.2A1.8 1.8 0 0 0 4.4 20h15.2a1.8 1.8 0 0 0 1.6-2.8L13.7 4.3a1.9 1.9 0 0 0-3.4 0z" />
  </Icon>
);

export const StarIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="m12 4 2.35 4.9 5.4.72-3.95 3.72.98 5.32L12 16.1l-4.78 2.56.98-5.32L4.25 9.62l5.4-.72z" />
  </Icon>
);
